import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, RefreshCw, MoreVertical,
  FolderOpen, GripVertical, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';

const categoryTypes = [
  { value: 'product', label: 'Product Category' },
  { value: 'service', label: 'Service Category' },
  { value: 'project', label: 'Project/Gallery Category' }
];

const defaultCategory = {
  id: '', name: '', slug: '', description: '', image: '',
  type: 'product', order: 0, is_active: true, show_on_homepage: false
};

export const CategoriesManager = () => {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await apiService.getCategories(token);
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await apiService.createCategory(token, item);
        toast.success('Category created successfully!');
      } else {
        await apiService.updateCategory(token, item.id, item);
        toast.success('Category updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving category');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteCategory(token, deleteDialog.item.id);
      toast.success('Category deleted');
      setDeleteDialog({ open: false, item: null });
      fetchCategories();
    } catch (error) {
      toast.error('Error deleting category');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultCategory, id: `cat-${Date.now()}`, order: categories.length + 1, isNew: true }
    });
  };

  const filtered = categories.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || c.type === filterType;
    return matchSearch && matchType;
  });

  const groupedCategories = {
    product: filtered.filter(c => c.type === 'product'),
    service: filtered.filter(c => c.type === 'service'),
    project: filtered.filter(c => c.type === 'project')
  };

  const stats = {
    total: categories.length,
    product: categories.filter(c => c.type === 'product').length,
    service: categories.filter(c => c.type === 'service').length,
    project: categories.filter(c => c.type === 'project').length
  };

  return (
    <div className="space-y-6" data-testid="categories-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Categories</h1>
          <p className="text-slate-500">Manage product, service, and project categories</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-sky-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-slate-500">Total</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Tag className="w-5 h-5 text-green-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.product}</p><p className="text-xs text-slate-500">Products</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Tag className="w-5 h-5 text-orange-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.service}</p><p className="text-xs text-slate-500">Services</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Tag className="w-5 h-5 text-purple-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.project}</p><p className="text-xs text-slate-500">Projects</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search categories..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {categoryTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchCategories}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Categories List */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No categories found</p>
          <Button onClick={() => openEditModal()} variant="outline" className="mt-4"><Plus className="w-4 h-4 mr-2" /> Add Category</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {/* Product Categories */}
          {groupedCategories.product.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4 text-green-600" /> Product Categories ({groupedCategories.product.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupedCategories.product.sort((a, b) => a.order - b.order).map((cat) => (
                  <CategoryCard key={cat.id} category={cat} onEdit={openEditModal} onDelete={setDeleteDialog} />
                ))}
              </div>
            </div>
          )}

          {/* Service Categories */}
          {groupedCategories.service.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4 text-orange-600" /> Service Categories ({groupedCategories.service.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupedCategories.service.sort((a, b) => a.order - b.order).map((cat) => (
                  <CategoryCard key={cat.id} category={cat} onEdit={openEditModal} onDelete={setDeleteDialog} />
                ))}
              </div>
            </div>
          )}

          {/* Project Categories */}
          {groupedCategories.project.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4 text-purple-600" /> Project Categories ({groupedCategories.project.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupedCategories.project.sort((a, b) => a.order - b.order).map((cat) => (
                  <CategoryCard key={cat.id} category={cat} onEdit={openEditModal} onDelete={setDeleteDialog} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Category' : 'Edit Category'}</DialogTitle>
            <DialogDescription>Fill in the category details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4">
              <ImageUploader
                label="Category Image (Optional)"
                value={editModal.item.image || ''}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, image: url } })}
                aspectRatio={16/9}
                token={token}
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category ID</Label>
                  <Input value={editModal.item.id} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={editModal.item.type} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, type: value } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{categoryTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Category Name *</Label>
                <Input
                  value={editModal.item.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const updates = { name };
                    if (editModal.item.isNew) {
                      updates.id = generateSlug(name);
                      updates.slug = generateSlug(name);
                    }
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Shower Enclosures"
                />
              </div>

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={editModal.item.description || ''}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, description: e.target.value } })}
                  placeholder="Brief description of this category"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={editModal.item.order}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })}
                />
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.is_active}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.show_on_homepage}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })}
                  />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.item?.name}"? Products using this category will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Category Card Component
const CategoryCard = ({ category, onEdit, onDelete }) => {
  const typeColors = {
    product: 'bg-green-100 text-green-700',
    service: 'bg-orange-100 text-orange-700',
    project: 'bg-purple-100 text-purple-700'
  };

  return (
    <Card className={`${!category.is_active ? 'opacity-60' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            {category.image ? (
              <img src={category.image} alt={category.name} className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-slate-900 truncate">{category.name}</h4>
                <span className="text-xs text-slate-400">#{category.order}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs rounded ${typeColors[category.type]}`}>
                  {category.type}
                </span>
                {!category.is_active && (
                  <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700">Inactive</span>
                )}
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="w-8 h-8">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(category)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete({ open: true, item: category })} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
};

export default CategoriesManager;
